import { useState, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import type { Producto } from '@/types/database'
import { formatMoney } from '@/lib/utils'
import {
  useProductos, useCategorias, useDepositos, useToggleActivo, useEliminarProducto,
  getStockTotal, getStockStatus, getPrecio,
} from './hooks'
import ProductoDrawer from './ProductoDrawer'
import AjusteStockModal from './AjusteStockModal'
import {
  PlusIcon, SearchIcon, EditIcon, EyeIcon, EyeOffIcon, TrashIcon, BoxIcon,
} from '@/components/shared/Icons'

export default function ProductosPage() {
  const { data: productos = [], isLoading, error } = useProductos()
  const { data: categorias = [] } = useCategorias()
  const { data: depositos = [] } = useDepositos()
  const toggleActivo = useToggleActivo()
  const eliminar = useEliminarProducto()

  const [busqueda, setBusqueda] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState('')
  const [soloActivos, setSoloActivos] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editando, setEditando] = useState<Producto | null>(null)
  const [ajustandoStock, setAjustandoStock] = useState<Producto | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Producto | null>(null)

  const filtrados = useMemo(() => {
    return productos.filter(p => {
      if (soloActivos && !p.activo) return false
      const matchBusqueda = !busqueda || (
        p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        p.marca?.toLowerCase().includes(busqueda.toLowerCase()) ||
        p.codigo_barras?.includes(busqueda) ||
        p.sku?.toLowerCase().includes(busqueda.toLowerCase())
      )
      const matchCategoria = !categoriaFiltro || p.categoria_id === categoriaFiltro
      return matchBusqueda && matchCategoria
    })
  }, [productos, busqueda, categoriaFiltro, soloActivos])

  function abrirNuevo() {
    setEditando(null)
    setDrawerOpen(true)
  }

  function abrirEditar(p: Producto) {
    setEditando(p)
    setDrawerOpen(true)
  }

  function cerrarDrawer() {
    setDrawerOpen(false)
    setEditando(null)
  }

  async function handleToggle(p: Producto) {
    try {
      await toggleActivo.mutateAsync({ id: p.id, activo: !p.activo })
      toast.success(p.activo ? 'Producto desactivado' : 'Producto activado')
    } catch {
      toast.error('Error al cambiar estado')
    }
  }

  async function handleDelete(p: Producto) {
    try {
      await eliminar.mutateAsync(p.id)
      toast.success('Producto eliminado')
      setConfirmDelete(null)
    } catch {
      toast.error('Error al eliminar')
    }
  }

  if (isLoading) return <PageSkeleton />

  if (error) return (
    <div style={{ color: 'var(--me-bad)', padding: 24 }}>
      Error al cargar productos
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 6 }}>Inventario</div>
          <h1 className="me-page-title">Productos</h1>
        </div>
        <button className="me-btn me-btn-primary" onClick={abrirNuevo}>
          <PlusIcon size={15} /> Nuevo producto
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 320 }}>
          <SearchIcon
            size={15}
            style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--me-text-3)' }}
          />
          <input
            className="me-input"
            style={{ width: '100%', paddingLeft: 34 }}
            type="text"
            placeholder="Buscar por nombre, marca, codigo..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setCategoriaFiltro('')} style={pillStyle(categoriaFiltro === '')}>
            Todos
          </button>
          {categorias.map(c => (
            <button
              key={c.id}
              onClick={() => setCategoriaFiltro(c.id === categoriaFiltro ? '' : c.id)}
              style={pillStyle(categoriaFiltro === c.id)}
            >
              {c.nombre}
            </button>
          ))}
        </div>

        <button
          onClick={() => setSoloActivos(v => !v)}
          style={{
            marginLeft: 'auto',
            padding: '5px 12px',
            borderRadius: 8,
            border: '1px solid var(--me-line-2)',
            background: soloActivos ? 'var(--me-surface)' : 'var(--me-surface-3)',
            fontSize: 12, fontWeight: 500, cursor: 'pointer',
            color: soloActivos ? 'var(--me-text-3)' : 'var(--me-text)',
            transition: 'all 0.1s',
          }}
        >
          {soloActivos ? 'Ver inactivos' : 'Ocultar inactivos'}
        </button>
      </div>

      {/* Stats rápidos */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <StatChip label="Total" value={productos.length} />
        <StatChip
          label="Sin stock"
          value={productos.filter(p => getStockStatus(p) === 'sin').length}
          color="var(--me-bad)"
        />
        <StatChip
          label="Stock bajo"
          value={productos.filter(p => getStockStatus(p) === 'bajo').length}
          color="var(--me-warn)"
        />
      </div>

      {/* Tabla */}
      {filtrados.length === 0 ? (
        <EmptyState busqueda={busqueda} soloActivos={soloActivos} onNuevo={abrirNuevo} />
      ) : (
        <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoria</th>
                <th style={{ textAlign: 'right' }}>P. Minorista</th>
                <th style={{ textAlign: 'right' }}>P. Mayorista</th>
                <th style={{ textAlign: 'center' }}>Stock</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtrados.map(p => (
                <ProductoRow
                  key={p.id}
                  producto={p}
                  onEditar={() => abrirEditar(p)}
                  onAjustarStock={() => setAjustandoStock(p)}
                  onToggle={() => handleToggle(p)}
                  onEliminar={() => setConfirmDelete(p)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer crear/editar */}
      {drawerOpen && (
        <ProductoDrawer producto={editando} onClose={cerrarDrawer} />
      )}

      {/* Modal ajuste stock */}
      {ajustandoStock && depositos.length > 0 && (
        <AjusteStockModal
          producto={ajustandoStock}
          depositos={depositos}
          onClose={() => setAjustandoStock(null)}
        />
      )}

      {/* Modal confirmar eliminacion */}
      {confirmDelete && (
        <>
          <div
            className="me-overlay-in"
            onClick={() => setConfirmDelete(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.28)' }}
          />
          <div
            className="me-modal-in"
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              zIndex: 41, width: 360,
              background: 'var(--me-surface)',
              borderRadius: 16,
              boxShadow: 'var(--me-shadow-modal)',
              padding: 24,
            }}
          >
            <h2 className="me-section-title" style={{ marginBottom: 8 }}>
              Eliminar producto
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--me-text-2)', margin: '0 0 20px' }}>
              Vas a eliminar <strong>{confirmDelete.nombre}</strong>. Esta accion no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="me-btn me-btn-ghost"
                onClick={() => setConfirmDelete(null)}
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                className="me-btn me-btn-danger"
                onClick={() => handleDelete(confirmDelete)}
                disabled={eliminar.isPending}
                style={{ flex: 1 }}
              >
                {eliminar.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function ProductoRow({ producto, onEditar, onAjustarStock, onToggle, onEliminar }: {
  producto: Producto
  onEditar: () => void
  onAjustarStock: () => void
  onToggle: () => void
  onEliminar: () => void
}) {
  const stockTotal = getStockTotal(producto)
  const status = getStockStatus(producto)
  const minorista = getPrecio(producto, 'minorista')
  const mayorista = getPrecio(producto, 'mayorista')

  return (
    <tr style={{ opacity: producto.activo ? 1 : 0.45 }}>
      <td>
        <div style={{ fontWeight: 500, fontSize: 13.5 }}>{producto.nombre}</div>
        {producto.marca && (
          <div style={{ fontSize: 12, color: 'var(--me-text-3)' }}>{producto.marca}</div>
        )}
        {producto.codigo_barras && (
          <div style={{ fontSize: 11, fontFamily: 'var(--me-mono)', color: 'var(--me-text-4)' }}>
            {producto.codigo_barras}
          </div>
        )}
      </td>
      <td>
        {producto.categorias ? (
          <span className="me-pill">{producto.categorias.nombre}</span>
        ) : (
          <span style={{ color: 'var(--me-text-4)', fontSize: 12 }}>—</span>
        )}
      </td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>
        {minorista > 0 ? `$${formatMoney(minorista)}` : <span style={{ color: 'var(--me-text-4)' }}>—</span>}
      </td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>
        {mayorista > 0 ? (
          <span style={{ color: 'var(--me-mayor)', fontWeight: 600 }}>
            ${formatMoney(mayorista)}
          </span>
        ) : (
          <span style={{ color: 'var(--me-text-4)' }}>—</span>
        )}
      </td>
      <td style={{ textAlign: 'center' }}>
        <button
          onClick={onAjustarStock}
          title="Ajustar stock"
          style={{
            fontFamily: 'var(--me-mono)',
            fontSize: 14,
            fontWeight: 600,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--me-text)',
            padding: '2px 6px',
            borderRadius: 6,
            transition: 'background var(--me-dur-short)',
          }}
          onMouseOver={e => (e.currentTarget.style.background = 'var(--me-surface-2)')}
          onMouseOut={e => (e.currentTarget.style.background = 'none')}
        >
          {stockTotal}
        </button>
      </td>
      <td style={{ textAlign: 'center' }}>
        <StockBadge status={status} minimo={producto.stock_minimo} />
      </td>
      <td>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <ActionBtn onClick={onEditar} title="Editar">
            <EditIcon size={14} />
          </ActionBtn>
          <ActionBtn onClick={onToggle} title={producto.activo ? 'Desactivar' : 'Activar'}>
            {producto.activo ? <EyeOffIcon size={14} /> : <EyeIcon size={14} />}
          </ActionBtn>
          <ActionBtn onClick={onEliminar} title="Eliminar" danger>
            <TrashIcon size={14} />
          </ActionBtn>
        </div>
      </td>
    </tr>
  )
}

function StockBadge({ status, minimo }: { status: 'ok' | 'bajo' | 'sin'; minimo: number }) {
  const map = {
    ok:   { label: 'OK',                   color: 'var(--me-ok)',   bg: 'var(--me-ok-soft)' },
    bajo: { label: `Bajo (min ${minimo})`, color: 'var(--me-warn)', bg: 'var(--me-warn-soft)' },
    sin:  { label: 'Sin stock',            color: 'var(--me-bad)',  bg: 'var(--me-bad-soft)' },
  }
  const s = map[status]
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.02em',
      color: s.color,
      background: s.bg,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  )
}

function ActionBtn({ children, onClick, title, danger }: {
  children: React.ReactNode
  onClick: () => void
  title: string
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: danger ? 'var(--me-bad)' : 'var(--me-text-3)',
        borderRadius: 6, padding: 5,
        display: 'flex', alignItems: 'center',
        opacity: danger ? 0.6 : 1,
        transition: 'background var(--me-dur-short), color var(--me-dur-short)',
      }}
      onMouseOver={e => {
        e.currentTarget.style.background = danger ? 'var(--me-bad-soft)' : 'var(--me-surface-2)'
        e.currentTarget.style.color = danger ? 'var(--me-bad)' : 'var(--me-text)'
        e.currentTarget.style.opacity = '1'
      }}
      onMouseOut={e => {
        e.currentTarget.style.background = 'none'
        e.currentTarget.style.color = danger ? 'var(--me-bad)' : 'var(--me-text-3)'
        e.currentTarget.style.opacity = danger ? '0.6' : '1'
      }}
    >
      {children}
    </button>
  )
}

function StatChip({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 10px',
      background: 'var(--me-surface)',
      border: '1px solid var(--me-line-2)',
      borderRadius: 8,
      fontSize: 12,
    }}>
      <span style={{ color: 'var(--me-text-3)' }}>{label}</span>
      <span style={{ fontWeight: 700, fontFamily: 'var(--me-mono)', color: color ?? 'var(--me-text)' }}>
        {value}
      </span>
    </div>
  )
}

function EmptyState({ busqueda, soloActivos, onNuevo }: {
  busqueda: string
  soloActivos: boolean
  onNuevo: () => void
}) {
  const msg = busqueda
    ? `No hay productos que coincidan con "${busqueda}"`
    : soloActivos
      ? 'Sin productos activos — usa "Ver inactivos" para ver los archivados'
      : 'Crea tu primer producto para empezar'

  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--me-text-3)' }}>
      <BoxIcon size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--me-text-2)' }}>
        {busqueda ? 'Sin resultados' : 'Sin productos'}
      </div>
      <div style={{ fontSize: 13, marginBottom: 20 }}>{msg}</div>
      {!busqueda && (
        <button className="me-btn me-btn-primary" onClick={onNuevo}>
          <PlusIcon size={15} /> Nuevo producto
        </button>
      )}
    </div>
  )
}

function PageSkeleton() {
  return (
    <div>
      <div style={{ height: 36, width: 160, background: 'var(--me-surface-3)', borderRadius: 8, marginBottom: 24 }} />
      <div style={{ height: 40, background: 'var(--me-surface-3)', borderRadius: 10, marginBottom: 16 }} />
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ height: 52, background: 'var(--me-surface-3)', borderRadius: 8, marginBottom: 6, opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  )
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: '5px 12px',
    borderRadius: 'var(--me-r-pill)',
    border: '1px solid',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all var(--me-dur-short)',
    borderColor: active ? 'var(--me-acid)' : 'var(--me-line-2)',
    background: active ? 'var(--me-acid)' : 'var(--me-surface)',
    color: active ? 'var(--me-acid-ink)' : 'var(--me-text-2)',
  }
}
